/**
 * SEO helpers — canonical URL builders and JSON-LD structured data.
 *
 * All functions here are deliberately free of runtime globals (`window`,
 * `document`) so they can run during static prerendering (vite-plugin-prerender,
 * react-snap) and future SSR without special casing.
 *
 * Source of truth for the site origin is `VITE_SITE_URL`, injected at build
 * time via Vite env vars. Falls back to `window.location.origin` in the
 * browser and to the production URL in non-browser environments.
 */
import type { Journal, JournalAuthor } from '../types/journalsProps';

/** Production site URL — last-resort fallback when neither env nor window is available. */
const PRODUCTION_URL = 'https://writevellum.com';

/**
 * CSS selector over the paywalled region of the journal reader, used in the
 * `hasPart` marker for premium articles' JSON-LD. Google's "Subscription and
 * paywalled content" guidance asks publishers to disclose WHICH part of the
 * page is gated, via a CSS selector pointing at that DOM region. This lets
 * the crawler distinguish legitimate paywall previews from cloaking.
 *
 * The selector below targets the literal className on `<JournalPremiumGate>`
 * — the component that renders when `isPremiumLocked` is true in
 * pages/journals/JournalReader.tsx. That component REPLACES the normal body
 * region for premium-locked viewers, so a single selector over the gate is
 * the paywalled region. MUI's sx styling does not produce stable class
 * names, which is why the gate carries a literal `className` for this one
 * purpose — see JournalPremiumGate.tsx. If either side of this contract
 * changes, both must move in the same commit or Google's paywall validator
 * will flag the page as potential cloaking.
 */
const PAYWALLED_CSS_SELECTOR = '.journal-premium-gate';

/**
 * Resolve the site's origin (protocol + host, no trailing slash).
 *
 * Precedence:
 *   1. `VITE_SITE_URL` build-time env var (explicit, wins everywhere)
 *   2. `window.location.origin` in the browser (dev & preview deploys)
 *   3. Hard-coded production URL as last resort (prerender/SSR without env)
 *
 * The env var should be set in Railway build args alongside VITE_API_URL so
 * that prerendered HTML ships with correct absolute URLs.
 */
export function getSiteOrigin(): string {
  const envUrl = import.meta.env.VITE_SITE_URL as string | undefined;
  if (envUrl) return envUrl.replace(/\/$/, '');
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin;
  }
  return PRODUCTION_URL;
}

/** Absolute URL for a journal's reader page. */
export function canonicalJournalUrl(username: string, slug: string): string {
  return `${getSiteOrigin()}/journals/${username}/${slug}`;
}

/** Absolute URL for a user profile. Uses the bare `/:username` form — the primary public path. */
export function canonicalProfileUrl(username: string): string {
  return `${getSiteOrigin()}/${username}`;
}

/** Absolute URL for a tag page. */
export function canonicalTagUrl(tag: string): string {
  return `${getSiteOrigin()}/tag/${encodeURIComponent(tag)}`;
}

/**
 * Best-effort description for a journal, trimmed to ~160 chars (below the OG
 * cutoff that `PageMeta` enforces anyway). Prefers the author-set meta
 * description, then the excerpt, then the raw text preview.
 */
export function journalDescription(journal: Journal): string {
  const candidate =
    journal.metaDescription?.trim() ||
    journal.excerpt?.trim() ||
    journal.rawText?.trim().slice(0, 300) ||
    '';
  // Collapse whitespace so line breaks in rawText don't become weird in the tag
  return candidate.replace(/\s+/g, ' ').trim();
}

/**
 * Build schema.org Article JSON-LD for a journal detail page.
 *
 * Google uses this for rich results (headline, author byline, published date
 * in SERP). Required fields per schema.org: `headline`, `author`, `datePublished`.
 * Image and publisher are strongly recommended.
 *
 * ── Paywalled content ────────────────────────────────────────────────────
 * When the journal is premium (`journal.isPremium === true`), we emit two
 * additional fields that Google's "Subscription and paywalled content"
 * guidance requires:
 *
 *   - `isAccessibleForFree: false` — declares the article is gated.
 *   - `hasPart` — a WebPageElement (also with isAccessibleForFree: false)
 *     carrying a CSS selector pointing at the DOM region that contains the
 *     paywalled body. This is how Google distinguishes a legitimate paywall
 *     preview from cloaking: the crawler fetches the page, sees the gate,
 *     and cross-checks the selector against its declared paywall region.
 *
 * Without these markers, a premium article's preview-to-crawler looks like
 * thin content at best and cloaking at worst — both cause ranking penalties
 * and may get the URL dropped from the index.
 *
 * Returned as a plain object — the caller stringifies it inside a
 * `<script type="application/ld+json">` tag (PageMeta handles this).
 */
export function buildArticleJsonLd(journal: Journal, absoluteUrl: string): Record<string, unknown> {
  const author = journal.author;
  const origin = getSiteOrigin();

  const base: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: journal.title,
    description: journalDescription(journal),
    ...(journal.coverImageUrl ? { image: [journal.coverImageUrl] } : {}),
    datePublished: journal.publishedAt ?? journal.createdAt,
    dateModified: journal.updatedAt,
    author: {
      '@type': 'Person',
      name: author.displayName ?? author.username,
      url: `${origin}/${author.username}`,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Vellum',
      url: origin,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': absoluteUrl,
    },
    ...(journal.tags && journal.tags.length > 0
      ? { keywords: journal.tags.map((t) => t.name).join(', ') }
      : {}),
    ...(typeof journal.wordCount === 'number' && journal.wordCount > 0
      ? { wordCount: journal.wordCount }
      : {}),
  };

  // Paywall markers — only added for premium journals. Free articles must
  // NOT carry `isAccessibleForFree: false`, or Google will treat them as
  // paywalled and expect preview behaviour that doesn't exist, which
  // degrades their ranking.
  if (journal.isPremium) {
    base.isAccessibleForFree = false;
    base.hasPart = {
      '@type': 'WebPageElement',
      isAccessibleForFree: false,
      cssSelector: PAYWALLED_CSS_SELECTOR,
    };
  }

  return base;
}

/**
 * Minimal profile shape accepted by `buildProfileJsonLd`. Accepts any object
 * with these fields — deliberately structural so we don't force callers to
 * import the full `UserProfile` type from pages/profile.
 */
export interface ProfileJsonLdInput {
  username: string;
  displayName?: string | null;
  bio?: string | null;
  avatar?: string | null;
  website?: string | null;
}

/**
 * Build schema.org ProfilePage JSON-LD wrapping a Person. ProfilePage is the
 * correct container type for user profiles per Google's documentation; the
 * nested Person carries the actual author identity.
 *
 * `@id` and `url` at the ProfilePage level give search engines a stable
 * identifier for the page itself (distinct from the Person entity it
 * describes). This mirrors how `buildArticleJsonLd` uses `mainEntityOfPage`
 * with `@id` to pin the article to its canonical URL.
 */
export function buildProfileJsonLd(
  profile: ProfileJsonLdInput,
  absoluteUrl: string,
): Record<string, unknown> {
  const name = profile.displayName ?? profile.username;
  const origin = getSiteOrigin();

  // sameAs is an array of URLs where this person also appears — their
  // personal website counts. Links like Twitter/Mastodon would also belong
  // here if we collected them.
  const sameAs: string[] = [];
  if (profile.website) sameAs.push(profile.website);

  return {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    '@id': absoluteUrl,
    url: absoluteUrl,
    mainEntity: {
      '@type': 'Person',
      name,
      alternateName: profile.username,
      ...(profile.bio ? { description: profile.bio } : {}),
      ...(profile.avatar ? { image: profile.avatar } : {}),
      url: `${origin}/${profile.username}`,
      ...(sameAs.length > 0 ? { sameAs } : {}),
    },
  };
}

/**
 * Minimal author-shape helper — re-exported for call sites that want to
 * narrow down `Journal['author']` without importing the full type.
 */
export type { JournalAuthor };
